import React, { useEffect, useMemo, useState, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import toast from 'react-hot-toast';
import { campusService, userService } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { User, Mail, ChevronRight, UserPlus, X, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import ListPageHeader from '../../components/list/ListPageHeader';
import ListToolbar from '../../components/list/ListToolbar';
import ListPagination from '../../components/list/ListPagination';
import ListEmptyState from '../../components/list/ListEmptyState';
import ListLoadingState from '../../components/list/ListLoadingState';
import DataRow, { DataRowList } from '../../components/list/DataRow';
import StatusPill from '../../components/list/StatusPill';
import { parsePaginated, DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers';
import AppSelect from '../../components/ui/AppSelect'

function AdminStudents() {
    const { user } = useAuth();
    const [students, setStudents] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [showInvite, setShowInvite] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviting, setInviting] = useState(false);

    const inviteCampusId = useMemo(() => user?.campus?.id ?? user?.campus, [user]);

    const fetchStudents = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: pageSize };
            if (statusFilter !== 'all') params.status = statusFilter;
            if (searchTerm) params.search = searchTerm;

            const res = await userService.getStudents(params);
            const { results, count } = parsePaginated(res);
            setStudents(results);
            setTotalCount(count);
        } catch (e) {
            toast.error('Failed to load students.');
        } finally {
            setLoading(false);
        }
    }, [searchTerm, statusFilter, page, pageSize]);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, statusFilter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchStudents();
        }, 300);
        return () => clearTimeout(timer);
    }, [fetchStudents]);

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteCampusId) {
            toast.error('No campus assigned to send invites from.');
            return;
        }
        setInviting(true);
        try {
            const res = await campusService.inviteStudent(inviteCampusId, inviteEmail.trim());
            toast.success(res.data?.message || 'Invitation sent.');
            setShowInvite(false);
            setInviteEmail('');
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to send invitation.');
        } finally {
            setInviting(false);
        }
    };

    const statusSelect = (
        <AppSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
            <option value="all">All status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
            <option value="inactive">Inactive</option>
        </AppSelect>
    );

    return (
        <DashboardLayout>
            <main className="page-container content-width ux-list-page">
                <ListPageHeader
                    kicker="People"
                    kickerIcon={Users}
                    title="All Students"
                    description="Manage and view all students registered on the platform."
                    actions={(
                        <button
                            type="button"
                            className="ux-btn-sm ux-btn-primary"
                            onClick={() => setShowInvite(true)}
                            style={{ padding: '0.65rem 1rem', fontSize: '0.875rem' }}
                        >
                            <UserPlus size={18} /> Invite student
                        </button>
                    )}
                />

                <ListToolbar
                    searchValue={searchTerm}
                    onSearchChange={setSearchTerm}
                    searchPlaceholder="Search students…"
                    filters={statusSelect}
                />

                {loading ? (
                    <ListLoadingState label="Loading students…" />
                ) : students.length === 0 ? (
                    <ListEmptyState
                        icon={User}
                        title="No students found"
                        description="Try adjusting your search or status filter."
                    />
                ) : (
                    <DataRowList>
                        {students.map((s, index) => {
                                const fullName = s.first_name || s.last_name
                                    ? `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim()
                                    : (s.username || 'Student');
                                const resolvedStatus = s.user_status || s.status || 'active';

                                return (
                                    <DataRow
                                        key={s.id}
                                        index={index}
                                        className="ux-student-row"
                                        leading={(
                                            <div className="ux-avatar">
                                                <User size={16} />
                                            </div>
                                        )}
                                        title={fullName}
                                        subtitle={(
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                <Mail size={12} /> {s.email}
                                            </span>
                                        )}
                                        meta={<span>{s.campus_name || '—'}</span>}
                                        badges={<StatusPill status={resolvedStatus} label={resolvedStatus} />}
                                        actions={(
                                            <Link
                                                to={`/admin/students/${s.id}`}
                                                className="ux-btn-sm ux-btn-ghost"
                                                style={{ textDecoration: 'none' }}
                                            >
                                                View <ChevronRight size={14} />
                                            </Link>
                                        )}
                                    />
                            );
                        })}
                    </DataRowList>
                )}

                {!loading && students.length > 0 && (
                    <ListPagination
                        page={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                    />
                )}
            </main>

            {showInvite && (
                <div className="cohort-modal-backdrop" onClick={() => !inviting && setShowInvite(false)} role="presentation">
                    <div className="cohort-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="invite-student-title">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
                            <h3 id="invite-student-title" style={{ margin: 0 }}>Invite student by email</h3>
                            <button type="button" className="btn btn-ghost" onClick={() => setShowInvite(false)} aria-label="Close">
                                <X size={18} />
                            </button>
                        </div>
                        <p className="text-secondary" style={{ marginBottom: 'var(--spacing-md)', fontSize: '0.95rem' }}>
                            Sends a magic link to register and join your organisation. They also get access to the public Learnly marketplace.
                        </p>
                        <form onSubmit={handleInvite}>
                            <div className="cohort-form-field">
                                <label htmlFor="invite-email">Student email</label>
                                <input
                                    id="invite-email"
                                    type="email"
                                    required
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="student@example.com"
                                />
                            </div>
                            <div className="cohort-modal-actions">
                                <button type="button" className="btn btn-outline" onClick={() => setShowInvite(false)} disabled={inviting}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={inviting}>
                                    {inviting ? 'Sending…' : 'Send invite'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}

export default AdminStudents;
